// src/components/ManageUsersDashboard.jsx

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import EditRoleModal from './EditRoleModal';
import DeleteRoleModal from './DeleteRoleModal';
import InviteModal from './InviteModal';
import InfoModal from './InfoModal';
import { useModules } from '../context/ModulesContext';
import './ManageUsersDashboard.css';

// ── RBAC error copy ───────────────────────────────────────────────────────────
// The role endpoints return technical validation strings. Everything the admin
// can actually act on gets a plain-language title + message here, surfaced as an
// InfoModal pop-out; anything unmapped falls back to generic copy rather than
// leaking raw server text.
const ROLE_ERROR_COPY = [
    [/already exists/i,                 'Name Already In Use',    'This role name is already in use, please choose something unique'],
    [/description must be/i,            'Role Description',       'Please describe the role with 10 or more characters'],
    [/description is required/i,        'Role Description',       'Please describe the role with 10 or more characters'],
    [/description contains invalid/i,   'Role Description',       'The description can only use letters, numbers, spaces and basic punctuation'],
    [/name must be/i,                   'Role Name',              'Role names must be between 2 and 100 characters'],
    [/name is required/i,               'Role Name',              'Please give this role a name'],
    [/name contains invalid/i,          'Role Name',              'Role names can only use letters, numbers, spaces, hyphens and underscores'],
    [/at least one permission/i,        'Permissions Required',   'Please select at least one permission for this role'],
    [/may not exceed/i,                 'Too Many Permissions',   'A role can hold a maximum of 50 permissions'],
    [/not grantable|permission key/i,   'Permission Unavailable', "One or more of the selected permissions isn't available on your plan"],
    [/system roles cannot be deleted/i, 'Built-In Role',          'Built-in roles cannot be deleted'],
];

const STATUS_ERROR_COPY = {
    401: ['Session Expired',    'Your session has expired — please sign in again'],
    403: ['Not Permitted',      "You don't have permission to manage roles"],
    404: ['Role Not Found',     'This role no longer exists — refresh the page and try again'],
    413: ['Role Too Large',     'This role is too large to save'],
};

/**
 * Turns a failed role-endpoint response into an Error carrying admin-readable
 * copy. `.title` is the pop-out heading, `.message` the body. Reads the JSON
 * body when there is one; never surfaces raw server text.
 */
async function roleError(res, fallbackTitle, fallbackMessage) {
    let serverError = '';
    try {
        const body = await res.json();
        serverError = body?.error || body?.message || '';
    } catch {
        /* no JSON body — fall through to status-code copy */
    }

    let [title, message] = [fallbackTitle, fallbackMessage];
    const matched = ROLE_ERROR_COPY.find(([pattern]) => pattern.test(serverError));
    if (matched) {
        [, title, message] = matched;
    } else if (STATUS_ERROR_COPY[res.status]) {
        [title, message] = STATUS_ERROR_COPY[res.status];
    }

    const err = new Error(message);
    err.title = title;
    return err;
}

export default function ManageUsersDashboard() {
    const { roles, user, entitlements, refreshEntitlements } = useAuth();
    const { modules: MODULE_REGISTRY } = useModules();
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');
    const activeTab = (tabParam === 'users' || tabParam === 'roles') ? tabParam : 'users';

    const [pending, setPending]             = useState([]);
    const [approved, setApproved]           = useState([]);
    const [orgRoles, setOrgRoles]           = useState([]);   // { id, name, ... } from /roles
    const [profileTemplates, setProfileTemplates] = useState([]); // { id, name } from /templates
    const [status, setStatus]               = useState('');
    const [sortBy, setSortBy]               = useState('username');
    const [order, setOrder]                 = useState('asc');
    const [actioningUserIDs, setActioningUserIDs] = useState(new Set());

    const startActioning  = (id) => setActioningUserIDs(s => new Set(s).add(id));
    const finishActioning = (id) => setActioningUserIDs(s => { const n = new Set(s); n.delete(id); return n; });

    const currentUsername = user?.email?.trim().toLowerCase() || '';

    const isAdminUser = roles.permissions.includes('users.manage');

    // ── RBAC state ────────────────────────────────────────────────────────────
    const [availablePermissions, setAvailablePermissions] = useState([]);
    const [selectedRole, setSelectedRole] = useState(null);
    const [showEditRolePopup, setShowEditRolePopup] = useState(false);
    const [showDeleteRolePopup, setShowDeleteRolePopup] = useState(false);
    const [expandedRoleId, setExpandedRoleId] = useState(null);
    const [roleDeleteError, setRoleDeleteError] = useState(null); // { count, names[] }
    const [roleActionError, setRoleActionError] = useState(null); // { title, message }

    // ── Users section state ───────────────────────────────────────────────────
    const [pendingExpanded, setPendingExpanded]       = useState(false);
    const [invitationsExpanded, setInvitationsExpanded] = useState(false);
    const [invitations, setInvitations]               = useState([]);
    const [showInviteModal, setShowInviteModal]       = useState(false);

    // ── API calls ─────────────────────────────────────────────────────────────

    const getPendingUsers = async () => {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/register/pending_users`, {
            method: 'GET', credentials: 'include',
        });
        if (!res.ok) throw new Error(`Failed to fetch pending users: ${res.statusText}`);
        return res.json();
    };

    const getApprovedUsers = async () => {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/approved_users`, {
            method: 'GET', credentials: 'include',
        });
        if (!res.ok) throw new Error(`Failed to fetch approved users: ${res.statusText}`);
        return res.json();
    };

    // Fetches org and platform roles for dropdowns.
    // orgRoles: [{ id, name, ... }] — id used as dropdown value, name used for display.
    const getRoles = async () => {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/roles?scope=all`, {
            method: 'GET', credentials: 'include',
        });
        if (!res.ok) throw new Error(`Failed to fetch roles: ${res.statusText}`);
        const resJson = await res.json();
        setOrgRoles(resJson.org ?? []);
    };

    // The org's profile templates, for the Invite box's Profile dropdown.
    // Sending an invite now means choosing the invitee's profile, so this list
    // is loaded for anyone who can invite — GET /templates allows users.manage
    // alongside the profiles permissions for exactly that reason.
    const getProfileTemplates = async () => {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/templates`, {
            method: 'GET', credentials: 'include',
        });
        if (!res.ok) throw new Error(`Failed to fetch profile templates: ${res.statusText}`);
        const data = await res.json();
        setProfileTemplates(
            (Array.isArray(data) ? data : []).map(t => ({ id: t.id, name: t.name })),
        );
    };

    const fetchPermissions = async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/permissions`, {
                method: 'GET', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!res.ok) return;
            const data = await res.json();
            setAvailablePermissions(Array.isArray(data) ? data : (data.permissions ?? []));
        } catch (err) {
            console.error('Failed to fetch permissions:', err);
        }
    };

    const handleCreateRole = async (name, description, permKeys, defaultModule) => {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/roles`, {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name, description, permissions: permKeys, defaultModule }),
        });
        if (!res.ok) {
            throw await roleError(res, 'Role Not Created', "That role couldn't be created. Please try again.");
        }
        const created = await res.json();
        setOrgRoles(prev => [...prev, created]);
    };

    const handleUpdateRole = async (roleID, name, description, permKeys, defaultModule) => {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/roles/${roleID}`, {
            method: 'PUT', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name, description, permissions: permKeys, defaultModule }),
        });
        if (!res.ok) {
            throw await roleError(res, 'Role Not Saved', "That role couldn't be saved. Please try again.");
        }
        const updated = await res.json();
        setOrgRoles(prev => prev.map(r => r.id === roleID ? updated : r));
    };

    const handleDeleteRoleAction = async (roleID) => {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/roles/${roleID}`, {
            method: 'DELETE', credentials: 'include',
        });
        if (res.status === 409) {
            const body = await res.json();
            const names = (body.users || []).map(u => {
                const match = approved.find(a => a.user_id === u.userID);
                return match ? match.name : u.userID;
            });
            setRoleDeleteError({ count: body.affectedUserCount, names });
            setShowDeleteRolePopup(false);
            return;
        }
        if (!res.ok) {
            throw await roleError(res, 'Role Not Deleted', "That role couldn't be deleted. Please try again.");
        }
        setOrgRoles(prev => prev.filter(r => r.id !== roleID));
    };

    const onAddRole = () => { setSelectedRole(null); setShowEditRolePopup(true); };
    const onEditRole = (role) => { setSelectedRole(role); setShowEditRolePopup(true); };
    const onDeleteRole = (role) => { setSelectedRole(role); setShowDeleteRolePopup(true); };

    const onSaveRole = async (role) => {
        try {
            if (selectedRole) {
                await handleUpdateRole(selectedRole.id, role.name, role.description, role.permissions, role.defaultModule);
            } else {
                await handleCreateRole(role.name, role.description, role.permissions, role.defaultModule);
            }
            await refreshEntitlements();
            setShowEditRolePopup(false);
        } catch (err) {
            console.error('Failed to save role:', err.message);
            // Re-throw so EditRoleModal shows the message inline and stays open
            // with the admin's input intact, instead of a browser alert.
            throw err;
        }
    };

    const onConfirmDeleteRole = async (roleID) => {
        try {
            await handleDeleteRoleAction(roleID);
            setShowDeleteRolePopup(false);
        } catch (err) {
            console.error('Failed to delete role:', err.message);
            // Re-throw so DeleteRoleModal shows the message inline.
            throw err;
        }
    };

    const approveUser = async (userID) => {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/user/approve`, {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userID }),
        });
        if (!res.ok) throw new Error(`Failed to approve: ${res.statusText}`);
        return res.json();
    };

    const rejectUser = async (userID) => {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/applications/reject`, {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userID }),
        });
        if (!res.ok) throw new Error(`Failed to reject: ${res.statusText}`);
        return res.json();
    };

    const suspendUser = async (userID) => {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/suspend_user`, {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userID }),
        });
        if (!res.ok) throw new Error(`Failed to suspend: ${res.statusText}`);
        return res.json();
    };

    const unsuspendUser = async (userID) => {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/unsuspend_user`, {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userID }),
        });
        if (!res.ok) throw new Error(`Failed to restore: ${res.statusText}`);
        return res.json();
    };

    const revokeUser = async (userID) => {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/revoke_user`, {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userID }),
        });
        if (!res.ok) throw new Error(`Failed to remove: ${res.statusText}`);
        return res.json();
    };

    // ── Data loading ──────────────────────────────────────────────────────────

    const isSelf = (u) => u.email?.trim().toLowerCase() === currentUsername;

    const loadAll = useCallback(async () => {
        setStatus('Loading users…');
        try {
            const [pendingUsers, approvedUsers] = await Promise.all([
                getPendingUsers().catch((e) => { console.error(e); return []; }),
                getApprovedUsers().catch((e) => { console.error(e); return []; }),
            ]);
            setPending(pendingUsers  ?? []);
            setApproved(approvedUsers ?? []);
            // Only clear on success — errors need the 3s timeout to be visible
            setStatus('');
        } catch (err) {
            setStatus(`Error: ${err.message}`);
        }
    }, []);

    const loadAllQueue = useRef(false);
    const safeLoadAll  = useCallback(() => {
        if (loadAllQueue.current) return;
        loadAllQueue.current = true;
        setTimeout(() => {
            loadAll().finally(() => { loadAllQueue.current = false; });
        }, 500);
    }, [loadAll]);

    const loadInvitations = useCallback(async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/invites`, {
                method: 'GET', credentials: 'include',
            });
            if (!res.ok) return;
            const data = await res.json();
            setInvitations(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error('Failed to load invitations:', e);
        }
    }, []);

    useEffect(() => {
        if (!isAdminUser) return;
        safeLoadAll();
        getRoles().catch((e) => console.error('Failed to load roles:', e));
        getProfileTemplates().catch((e) => console.error('Failed to load profile templates:', e));
        fetchPermissions();
        loadInvitations();
    }, [isAdminUser, safeLoadAll, loadInvitations]);

    useEffect(() => {
        if (!status) return;
        const t = setTimeout(() => setStatus(''), 3000);
        return () => clearTimeout(t);
    }, [status]);


    // ── Sorting ───────────────────────────────────────────────────────────────

    const toggleSort = useCallback((col) => {
        setSortBy(prev => {
            if (prev === col) {
                setOrder(o => o === 'asc' ? 'desc' : 'asc');
                return prev;
            }
            setOrder('asc');
            return col;
        });
    }, []);

    const sortedPending = useMemo(() => [...pending].sort((a, b) => {
        let vA, vB;
        switch (sortBy) {
            case 'name':        vA = `${a.firstName} ${a.lastName}`.toLowerCase(); vB = `${b.firstName} ${b.lastName}`.toLowerCase(); break;
            case 'email':       vA = a.email.toLowerCase(); vB = b.email.toLowerCase(); break;
            case 'signup_time': vA = new Date(a.submittedAt); vB = new Date(b.submittedAt); break;
            default:            vA = ''; vB = '';
        }
        return (vA < vB ? -1 : vA > vB ? 1 : 0) * (order === 'asc' ? 1 : -1);
    }), [pending, sortBy, order]);

    const sortedApproved = useMemo(() => [...approved].sort((a, b) => {
        let vA, vB;
        switch (sortBy) {
            case 'firstName':   vA = (a.firstName || '').toLowerCase(); vB = (b.firstName || '').toLowerCase(); break;
            case 'lastName':    vA = (a.lastName || '').toLowerCase(); vB = (b.lastName || '').toLowerCase(); break;
            case 'phone':       vA = (a.phone || '').toLowerCase(); vB = (b.phone || '').toLowerCase(); break;
            case 'username':    vA = a.email.toLowerCase(); vB = b.email.toLowerCase(); break;
            case 'role':        vA = (a.platformRole || 'User').toLowerCase(); vB = (b.platformRole || 'User').toLowerCase(); break;
            case 'suspended':   vA = a.status === 'suspended' ? 1 : 0; vB = b.status === 'suspended' ? 1 : 0; break;
            case 'signup_time': vA = new Date(a.created_at); vB = new Date(b.created_at); break;
            case 'last_login':  vA = new Date(a.last_login || 0); vB = new Date(b.last_login || 0); break;
            default:            vA = ''; vB = '';
        }
        return (vA < vB ? -1 : vA > vB ? 1 : 0) * (order === 'asc' ? 1 : -1);
    }), [approved, sortBy, order]);

    // The backend only attaches `phone` to members when the org enforces MFA, so
    // the column is shown exactly when that field is present on the user objects.
    const _showPhone = useMemo(
        () => approved.some(u => Object.prototype.hasOwnProperty.call(u, 'phone')),
        [approved],
    );

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleApprove = async (pendingUser) => {
        startActioning(pendingUser.id);
        setStatus(`Approving ${pendingUser.email}…`);
        try {
            await approveUser(pendingUser.id);
            safeLoadAll();
        } catch (err) {
            setStatus(`Error approving ${pendingUser.email}: ${err.message}`);
        } finally {
            finishActioning(pendingUser.id);
        }
    };

    const handleReject = async (pendingUser) => {
        startActioning(pendingUser.id);
        setStatus(`Rejecting ${pendingUser.email}…`);
        try {
            await rejectUser(pendingUser.id);
            safeLoadAll();
        } catch (err) {
            setStatus(`Error rejecting ${pendingUser.email}: ${err.message}`);
        } finally {
            finishActioning(pendingUser.id);
        }
    };

    // /approved_users returns customRoles as an array of role IDs — there is no
    // customRoleIDs field. All reads, optimistic updates, and rollbacks use
    // customRoles as the ID array, and the dropdown/display both work with IDs,
    // resolving names on the fly via orgRoles.find().
    const handleOrgRoleUpdate = async (u, newRoleID) => {
        const previousIDs = u.customRoles ?? [];
        const newIDs      = newRoleID ? [newRoleID] : [];

        startActioning(u.user_id);
        setStatus(`Updating org role for ${u.name}…`);

        // Optimistic update — store IDs in customRoles, consistent with API shape.
        // Both the dropdown value and readonly display resolve names via orgRoles.find().
        setApproved(prev => prev.map(x =>
            x.user_id === u.user_id ? { ...x, customRoles: newIDs } : x
        ));

        try {
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/roles/users/${u.user_id}/roles/custom`, {
                method: 'PUT',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ roleIDs: newIDs }),
            });
            if (!res.ok) {
                throw await roleError(res, 'Role Not Updated', `${u.name}'s role couldn't be updated. Please try again.`);
            }
            safeLoadAll();
        } catch (err) {
            // Rollback to previous IDs on failure
            setApproved(prev => prev.map(x =>
                x.user_id === u.user_id ? { ...x, customRoles: previousIDs } : x
            ));
            setStatus('');
            setRoleActionError({ title: err.title || 'Role Not Updated', message: err.message });
        } finally {
            finishActioning(u.user_id);
        }
    };

    const handleSuspend = async (u) => {
        startActioning(u.user_id);
        setStatus(`Suspending ${u.name}…`);
        try {
            await suspendUser(u.user_id);
            setApproved(prev => prev.map(x => x.user_id === u.user_id ? { ...x, status: 'suspended' } : x));
            safeLoadAll();
        } catch (err) {
            setStatus(`Error suspending: ${err.message}`);
        } finally {
            finishActioning(u.user_id);
        }
    };

    const handleRestore = async (u) => {
        startActioning(u.user_id);
        setStatus(`Restoring ${u.name}…`);
        try {
            await unsuspendUser(u.user_id);
            setApproved(prev => prev.map(x => x.user_id === u.user_id ? { ...x, status: 'active' } : x));
            safeLoadAll();
        } catch (err) {
            setStatus(`Error restoring: ${err.message}`);
        } finally {
            finishActioning(u.user_id);
        }
    };

    const handleDelete = async (u) => {
        startActioning(u.user_id);
        setStatus(`Removing ${u.name}…`);
        try {
            await revokeUser(u.user_id);
            setApproved(prev => prev.filter(x => x.user_id !== u.user_id));
            safeLoadAll();
        } catch (err) {
            setStatus(`Error removing: ${err.message}`);
        } finally {
            finishActioning(u.user_id);
        }
    };

    if (!isAdminUser) return <p>You do not have permission to view this page.</p>;

    return (
        <div className="manage-users">

            {/* ── Tabs ── */}
            <div className="template-tabs">
                <div
                    className={`template-tab ${activeTab === 'users' ? 'active' : ''}`}
                    onClick={() => setSearchParams({ tab: 'users' })}
                >
                    Users
                </div>
                {activeTab === 'users' && (
                    <div
                        className="smrtcop-tab smrtcop-tab-plus"
                        title="Invite User"
                        onClick={() => setShowInviteModal(true)}
                    >
                        +
                    </div>
                )}
                <div
                    className={`template-tab ${activeTab === 'roles' ? 'active' : ''}`}
                    onClick={() => setSearchParams({ tab: 'roles' })}
                >
                    Roles
                </div>
                {activeTab === 'roles' && (
                    <div
                        className="smrtcop-tab smrtcop-tab-plus"
                        title="Add Role"
                        onClick={onAddRole}
                    >
                        +
                    </div>
                )}
            </div>

            {activeTab === 'roles' ? (() => {
                // Permission key → display name lookup
                const permNameMap = {};
                for (const p of (availablePermissions || [])) {
                    permNameMap[p.key] = p.name;
                }

                // Build allowlist: permission keys for entitled modules + system permissions
                const allowedKeys = new Set();
                // Track which keys are admin slots so we can also admit
                // sub-features that hang off them (e.g., profiles.sensitive
                // has parentKey "profiles.admin"). Without this, the outer
                // filter strips Admin+ sub-features before EditRoleModal
                // ever sees them and they never appear in the role editor.
                const adminPermissionKeys = new Set();
                // Same for view slots, so user-facing sub-features (chatbot.data
                // hangs off `chatbot`) survive this filter too.
                const viewPermissionKeys = new Set();
                for (const m of MODULE_REGISTRY) {
                    const entitled = m.entitlementKey === null || entitlements?.features?.[m.entitlementKey]?.enabled === true;
                    if (entitled) {
                        if (m.viewPermission) {
                            allowedKeys.add(m.viewPermission);
                            viewPermissionKeys.add(m.viewPermission);
                        }
                        if (m.adminPermission) {
                            allowedKeys.add(m.adminPermission);
                            adminPermissionKeys.add(m.adminPermission);
                        }
                    }
                }
                // Pass 2: admit sub-features whose parent is an entitled
                // module's adminPermission. Mirrors EditRoleModal's inner
                // filter so Admin+ sub-features survive both layers and
                // render under the parent module's Admin Access group.
                for (const p of (availablePermissions || [])) {
                    if (p.parentKey && adminPermissionKeys.has(p.parentKey)) {
                        allowedKeys.add(p.key);
                    }
                    // User-side sub-features, gated on the row's own category so
                    // the legacy chatbot rows (documents, queries, content.manage)
                    // that also parent to `chatbot` don't flood the picker.
                    if (
                        p.parentKey &&
                        viewPermissionKeys.has(p.parentKey) &&
                        p.category === 'User Access'
                    ) {
                        allowedKeys.add(p.key);
                    }
                }

                // Sort: Owner first, Member second, rest alphabetical
                // System roles first, then the admin-created ones, each block
                // alphabetical. Owner and Member stay pinned above the other
                // system roles — they are the two every org is built on.
                //
                // Ranked rather than compared pairwise: the previous version
                // returned -1 for "a is Owner" without looking at b, so the
                // comparator disagreed with itself and only behaved because
                // there is exactly one Owner.
                const roleRank = (role) => {
                    if (!role.isSystem) return 3;
                    if (role.name === 'Owner') return 0;
                    if (role.name === 'Member') return 1;
                    return 2;
                };
                const sortedRoles = [...orgRoles].sort(
                    (a, b) => roleRank(a) - roleRank(b) || a.name.localeCompare(b.name),
                );

                // Seeded, fixed roles — shown in blue, delete disabled.
                //
                // Read from the flag the backend sets, not a list of names. It
                // used to hardcode Owner/Member/Collaborator, so Delegate —
                // seeded with isSystem exactly the same way — drew as an
                // admin-created role with a live delete button, while the API
                // and both modals refused the operation. Any system role seeded
                // from here on is covered without touching this line.
                const isProtected = (role) => !!role.isSystem;

                return (
                <div>
                    <div className="table-responsive">
                        <table className="approved-table" style={{ width: '100%' }}>
                            <thead>
                                <tr>
                                    <th>Role</th>
                                    <th>Description</th>
                                    <th style={{ textAlign: 'center' }}>Permissions</th>
                                    <th style={{ textAlign: 'center' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedRoles.length === 0 ? (
                                    <tr><td colSpan={4} style={{ textAlign: 'center', color: '#666', padding: '1rem' }}>No roles defined yet.</td></tr>
                                ) : sortedRoles.map(role => (
                                    <React.Fragment key={role.id}>
                                        <tr style={{ transition: 'background 0.15s' }} onMouseEnter={e => e.currentTarget.style.background = '#1c1c1c'} onMouseLeave={e => e.currentTarget.style.background = ''}>
                                            <td style={{ color: isProtected(role) ? '#007bff' : undefined }}>
                                                {role.name}
                                            </td>
                                            <td style={{ color: '#aaa', fontSize: '0.85rem' }}>{role.description || '—'}</td>
                                            <td style={{ textAlign: 'center', fontSize: '0.85rem' }}>
                                                {(role.permissions || []).filter(k => allowedKeys.has(k)).length}
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                                                    {/* View / expand */}
                                                    <button
                                                        className={`smrtcop-btn-icon smrtcop-btn-view`}
                                                        title={expandedRoleId === role.id ? "Collapse" : "View permissions"}
                                                        onClick={() => setExpandedRoleId(expandedRoleId === role.id ? null : role.id)}
                                                    >
                                                        👁
                                                    </button>
                                                    {/* Edit — all roles editable. System roles (any with
                                                        isSystem: Owner, Member, Collaborator, Delegate)
                                                        restrict editing to the Description and Default
                                                        Landing Module inside the modal. */}
                                                    <button
                                                        className="smrtcop-btn-icon smrtcop-btn-edit"
                                                        title="Edit role"
                                                        onClick={() => onEditRole(role)}
                                                    >
                                                        <span style={{ display: 'inline-block', transform: 'rotate(-45deg)' }}>✏</span>
                                                    </button>
                                                    {/* Delete */}
                                                    <button
                                                        className={`smrtcop-btn-icon ${isProtected(role) ? '' : 'smrtcop-btn-delete'}`}
                                                        style={isProtected(role) ? { background: '#555', borderColor: '#555', cursor: 'not-allowed', opacity: 0.5 } : undefined}
                                                        title={isProtected(role) ? "System role" : "Delete role"}
                                                        onClick={isProtected(role) ? undefined : () => onDeleteRole(role)}
                                                        disabled={isProtected(role)}
                                                    >
                                                        🗑
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                        {/* Expanded permissions row */}
                                        {expandedRoleId === role.id && (() => {
                                            const visiblePerms = (role.permissions || []).filter(key => allowedKeys.has(key));

                                            // Build perm key → module lookup
                                            const permToMod = {};
                                            for (const m of MODULE_REGISTRY) {
                                                if (m.viewPermission) permToMod[m.viewPermission] = m;
                                                if (m.adminPermission) permToMod[m.adminPermission] = m;
                                            }

                                            // Build key → parentKey lookup so sub-features (e.g.,
                                            // profiles.sensitive → profiles.admin) can be routed to
                                            // the same group as their parent module. Without this,
                                            // sub-features belong to no bucket and silently
                                            // disappear from the expanded permissions row.
                                            const parentByKey = {};
                                            for (const p of (availablePermissions || [])) {
                                                if (p.parentKey) parentByKey[p.key] = p.parentKey;
                                            }

                                            // Split into user (view) and admin sections. Direct
                                            // module-slot matches first, then sub-features admitted
                                            // to the admin bucket when their parent is a module's
                                            // adminPermission (mirrors the EditRoleModal filter).
                                            // A key that IS one of a module's own slots is settled
                                            // by which slot it is, and never falls through to the
                                            // parent test below. Every admin permission carries
                                            // parentKey = the module's VIEW permission (see
                                            // deriveModulePermissionRows), so without this a
                                            // "Manage X" row passed the parent test too and showed
                                            // under User Access as well as Admin Access.
                                            //
                                            // The parent test is only for sub-features — keys that
                                            // are nobody's module slot, like chatbot.data hanging
                                            // off chatbot.
                                            const userPerms = visiblePerms.filter(k => {
                                                const m = permToMod[k];
                                                if (m) return m.viewPermission === k;
                                                const pk = parentByKey[k];
                                                return pk && permToMod[pk]?.viewPermission === pk;
                                            });
                                            const adminPerms = visiblePerms.filter(k => {
                                                const m = permToMod[k];
                                                if (m) return m.adminPermission === k;
                                                const pk = parentByKey[k];
                                                return pk && permToMod[pk]?.adminPermission === pk;
                                            });

                                            // Group by module group, sorted by displayOrder. For
                                            // sub-feature keys, look the module up via parentKey so
                                            // they nest under the parent's group header.
                                            const buildGroups = (keys) => {
                                                const map = {};
                                                for (const k of keys) {
                                                    const m = permToMod[k] || permToMod[parentByKey[k]];
                                                    if (!m) continue;
                                                    if (!map[m.name]) map[m.name] = { group: m.name, displayOrder: m.displayOrder, keys: [] };
                                                    if (m.displayOrder < map[m.name].displayOrder) map[m.name].displayOrder = m.displayOrder;
                                                    map[m.name].keys.push(k);
                                                }
                                                return Object.values(map).sort((a, b) => a.displayOrder - b.displayOrder);
                                            };

                                            const userGroups = buildGroups(userPerms);
                                            const adminGroups = buildGroups(adminPerms);

                                            // Find the viewPermission key for the role's defaultModule (fall back to org entitlements default)
                                            const effectiveDefault = role.defaultModule || entitlements?.defaultModule || 'chatbot';
                                            const defaultModulePerm = MODULE_REGISTRY.find(m => m.key === effectiveDefault)?.viewPermission;

                                            const renderSection = (label, color, groups) => groups.length > 0 && (
                                                <div>
                                                    <div style={{ fontSize: '0.7rem', color, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.5rem', borderBottom: '1px solid #2a2a2a', paddingBottom: '0.25rem' }}>{label}</div>
                                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '6px' }}>
                                                        {groups.flatMap(g => g.keys).map(k => {
                                                            const isDefault = k === defaultModulePerm;
                                                            return (
                                                                <span key={k} title={isDefault ? 'Default Landing Page' : undefined} style={{ fontSize: '0.8rem', background: isDefault ? '#1a3a2a' : '#2a2a2a', color: isDefault ? '#4caf50' : '#fff', border: isDefault ? '1px solid #4caf50' : '1px solid transparent', padding: '4px 10px', borderRadius: '4px', textAlign: 'center', cursor: isDefault ? 'help' : undefined }}>
                                                                    {permNameMap[k] || k}{isDefault ? ' ★' : ''}
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );

                                            return (
                                            <tr>
                                                <td colSpan={4} style={{ background: '#1a1a1a', padding: '16px 24px' }}>
                                                    {visiblePerms.length > 0 ? (
                                                        <div style={{ display: 'flex', gap: '3rem' }}>
                                                            <div style={{ flex: 1 }}>{renderSection('User Access', '#007bff', userGroups)}</div>
                                                            <div style={{ flex: 1 }}>{renderSection('Admin Access', '#d4a017', adminGroups)}</div>
                                                        </div>
                                                    ) : (
                                                        <span style={{ color: '#666', fontSize: '0.85rem' }}>No permissions assigned</span>
                                                    )}
                                                </td>
                                            </tr>
                                            );
                                        })()}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {showEditRolePopup && (
                        <EditRoleModal
                            roleObj={selectedRole}
                            availablePermissions={(availablePermissions || []).filter(p => allowedKeys.has(p.key))}
                            entitlements={entitlements}
                            isOpen={showEditRolePopup}
                            onClose={() => setShowEditRolePopup(false)}
                            onSave={onSaveRole}
                        />
                    )}

                    {showDeleteRolePopup && selectedRole && (
                        <DeleteRoleModal
                            roleObj={selectedRole}
                            isOpen={showDeleteRolePopup}
                            onClose={() => setShowDeleteRolePopup(false)}
                            onDelete={onConfirmDeleteRole}
                        />
                    )}

                    {roleDeleteError && (
                        <div className="smrtdocs-modal-overlay">
                            <div className="smrtdocs-modal" style={{ width: '440px' }}>
                                <div className="smrtdocs-modal-header">
                                    <h2 style={{ margin: 0, fontSize: '1rem' }}>Cannot Delete Role</h2>
                                </div>
                                <div className="smrtdocs-modal-body">
                                    <p style={{ margin: '0 0 0.75rem', fontSize: '0.9rem' }}>
                                        All users must be reassigned before you can delete this role.
                                        <br />This role is currently assigned to:
                                    </p>
                                    <ul style={{ margin: '0 0 0.5rem', paddingLeft: '1.2rem', fontSize: '0.85rem' }}>
                                        {roleDeleteError.names.map((name, i) => (
                                            <li key={i}>{name}</li>
                                        ))}
                                    </ul>
                                    {roleDeleteError.count > roleDeleteError.names.length && (
                                        <p style={{ margin: 0, fontSize: '0.8rem', color: '#9aa0a6' }}>
                                            + {roleDeleteError.count - roleDeleteError.names.length} more user{roleDeleteError.count - roleDeleteError.names.length !== 1 ? 's' : ''}
                                        </p>
                                    )}
                                </div>
                                <div style={{ padding: '12px 20px', display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #555' }}>
                                    <button className="btn-cancel" onClick={() => setRoleDeleteError(null)}>Close</button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                );
            })() : (
            <>
            {status && <p className="status">{status}</p>}

            {/* ── Pending Sign-ups (collapsible) ── */}
            <div style={{ marginBottom: 12 }}>
                <div
                    style={{
                        background: '#1a1a1a',
                        padding: '10px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        borderRadius: pendingExpanded ? '6px 6px 0 0' : 6,
                        cursor: 'pointer',
                        userSelect: 'none',
                    }}
                    onClick={() => setPendingExpanded(v => !v)}
                >
                    <span style={{ fontSize: '0.8rem', color: '#9aa0a6' }}>{pendingExpanded ? '▼' : '▶'}</span>
                    <h2 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600, color: '#cbd5e1' }}>
                        Pending Sign-ups
                        {pending.length > 0 && (
                            <span style={{ marginLeft: 8, background: '#c62828', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: '0.75rem', fontWeight: 700 }}>
                                {pending.length}
                            </span>
                        )}
                    </h2>
                </div>
                {pendingExpanded && (
                    <div style={{ background: '#1e1e1e', borderRadius: '0 0 6px 6px', padding: '16px 16px 8px' }}>
                        {pending.length === 0 ? (
                            <p className="users-empty-state">No pending sign-ups.</p>
                        ) : (
                            <div className="table-responsive">
                                <table className="pending-table">
                                    <thead>
                                        <tr>
                                            <th onClick={() => toggleSort('name')}>Name {sortBy === 'name' ? (order === 'asc' ? '▲' : '▼') : ''}</th>
                                            <th onClick={() => toggleSort('email')}>Email {sortBy === 'email' ? (order === 'asc' ? '▲' : '▼') : ''}</th>
                                            <th onClick={() => toggleSort('signup_time')}>Signup Date {sortBy === 'signup_time' ? (order === 'asc' ? '▲' : '▼') : ''}</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedPending.map((u) => (
                                            <tr key={u.id}>
                                                <td>{u.firstName} {u.lastName}</td>
                                                <td>{u.email}</td>
                                                <td>{new Date(u.submittedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}</td>
                                                <td>
                                                    {isSelf(u) ? '—' : (
                                                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                                                            <EmojiButton emoji="✅" disabled={actioningUserIDs.has(u.id)} onClick={() => handleApprove(u)} />
                                                            <EmojiButton emoji="❌" disabled={actioningUserIDs.has(u.id)} onClick={() => handleReject(u)} />
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ── Invitations (collapsible) ── */}
            <div style={{ marginBottom: 20 }}>
                <div
                    style={{
                        background: '#1a1a1a',
                        padding: '10px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        borderRadius: invitationsExpanded ? '6px 6px 0 0' : 6,
                        cursor: 'pointer',
                        userSelect: 'none',
                    }}
                    onClick={() => setInvitationsExpanded(v => !v)}
                >
                    <span style={{ fontSize: '0.8rem', color: '#9aa0a6' }}>{invitationsExpanded ? '▼' : '▶'}</span>
                    <h2 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600, color: '#cbd5e1' }}>
                        Invitations
                        {invitations.filter(inv => new Date(inv.expiresAt) > new Date()).length > 0 && (
                            <span style={{ marginLeft: 8, background: '#218838', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: '0.75rem', fontWeight: 700 }}>
                                {invitations.filter(inv => new Date(inv.expiresAt) > new Date()).length}
                            </span>
                        )}
                    </h2>
                </div>
                {invitationsExpanded && (
                    <div style={{ background: '#1e1e1e', borderRadius: '0 0 6px 6px', padding: '16px 16px 8px' }}>
                        {invitations.length === 0 ? (
                            <p className="users-empty-state">No invitations. Use the Invite button above to send one.</p>
                        ) : (
                            <div className="table-responsive">
                                <table className="approved-table">
                                    <thead>
                                        <tr>
                                            <th>Email</th>
                                            <th>Role</th>
                                            <th style={{ whiteSpace: 'nowrap', minWidth: '155px' }}>Invited On</th>
                                            <th style={{ whiteSpace: 'nowrap', minWidth: '155px' }}>Expiry Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invitations.map((inv) => {
                                            const expired = new Date(inv.expiresAt) < new Date();
                                            return (
                                                <tr key={inv.id}>
                                                    <td>{inv.email}</td>
                                                    <td>{orgRoles.find(r => r.id === inv.role)?.name ?? inv.role}</td>
                                                    <td style={{ whiteSpace: 'nowrap' }}>{new Date(inv.invitedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}</td>
                                                    <td style={{ whiteSpace: 'nowrap', color: expired ? '#e57373' : '#4caf50' }}>
                                                        {new Date(inv.expiresAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                                                    </td>
                                                    <td>
                                                        <div style={{ display: 'flex', gap: '8px' }}>
                                                            {expired ? (
                                                                // Cancelled / expired → admin can restart the timer
                                                                // (Resend) or permanently remove the request (Delete).
                                                                <>
                                                                    <button className="action-btn resend" onClick={async () => {
                                                                        try {
                                                                            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/invites/${inv.id}/resend`, { method: 'POST', credentials: 'include' });
                                                                            if (!res.ok) throw new Error((await res.json()).message);
                                                                            setStatus(`Invite resent to ${inv.email}.`);
                                                                            loadInvitations();
                                                                        } catch (err) { setStatus(`Failed to resend: ${err.message}`); }
                                                                    }}>Resend</button>
                                                                    <button className="action-btn delete" onClick={async () => {
                                                                        try {
                                                                            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/invites/${inv.id}`, { method: 'DELETE', credentials: 'include' });
                                                                            if (!res.ok) throw new Error((await res.json()).message);
                                                                            setStatus(`Invitation for ${inv.email} deleted.`);
                                                                            loadInvitations();
                                                                        } catch (err) { setStatus(`Failed to delete: ${err.message}`); }
                                                                    }}>Delete</button>
                                                                </>
                                                            ) : (
                                                                // Active invite → Cancel expires it immediately, which
                                                                // flips this row into the Resend / Delete state above.
                                                                <button className="action-btn suspend" onClick={async () => {
                                                                    try {
                                                                        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/invites/${inv.id}/expire`, { method: 'POST', credentials: 'include' });
                                                                        if (!res.ok) throw new Error((await res.json()).message);
                                                                        setStatus(`Invite for ${inv.email} cancelled.`);
                                                                        loadInvitations();
                                                                    } catch (err) { setStatus(`Failed to cancel: ${err.message}`); }
                                                                }}>Cancel</button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ── Members (always open) ── */}
            <div className="table-responsive">
                <table className="approved-table">
                    <thead>
                        <tr>
                            <th onClick={() => toggleSort('firstName')}>First Name {sortBy === 'firstName' ? (order === 'asc' ? '▲' : '▼') : ''}</th>
                            <th onClick={() => toggleSort('lastName')}>Last Name {sortBy === 'lastName' ? (order === 'asc' ? '▲' : '▼') : ''}</th>
                            <th onClick={() => toggleSort('username')}>Email {sortBy === 'username' ? (order === 'asc' ? '▲' : '▼') : ''}</th>
                            <th>Phone</th>
                            <th onClick={() => toggleSort('role')}>Role {sortBy === 'role' ? (order === 'asc' ? '▲' : '▼') : ''}</th>
                            <th onClick={() => toggleSort('suspended')}>Suspended {sortBy === 'suspended' ? (order === 'asc' ? '▲' : '▼') : ''}</th>
                            <th onClick={() => toggleSort('signup_time')}>Signup Date {sortBy === 'signup_time' ? (order === 'asc' ? '▲' : '▼') : ''}</th>
                            <th onClick={() => toggleSort('last_login')}>Last Login {sortBy === 'last_login' ? (order === 'asc' ? '▲' : '▼') : ''}</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedApproved.map((u) => {
                            const self           = isSelf(u);
                            const platformRole   = u.platformRole || 'User';
                            const isBillingAdmin = platformRole === 'Billing-Admin';
                            const canEdit        = !self && !isBillingAdmin;
                            const isActioning    = actioningUserIDs.has(u.user_id);
                            const currentOrgRoleID = (u.customRoles ?? [])[0] ?? '';
                            return (
                                <tr key={u.user_id}>
                                    <td>{u.firstName || '-'}</td>
                                    <td>{u.lastName || '-'}</td>
                                    <td>{u.email}</td>
                                    <td>{u.phone || '—'}</td>
                                    <td className="roles-cell">
                                        {canEdit ? (
                                            orgRoles.length === 0 ? (
                                                <span className="role-empty">No roles defined</span>
                                            ) : (
                                                <select
                                                    value={currentOrgRoleID}
                                                    disabled={isActioning}
                                                    onChange={(e) => handleOrgRoleUpdate(u, e.target.value)}
                                                    className="role-select"
                                                >
                                                    {orgRoles.filter(role => role.name !== 'Owner').map(role => (
                                                        <option key={role.id} value={role.id}>{role.name}</option>
                                                    ))}
                                                </select>
                                            )
                                        ) : (
                                            <span>
                                                {(u.customRoles ?? [])
                                                    .map(id => orgRoles.find(r => r.id === id)?.name ?? id)
                                                    .join(', ') || '—'}
                                            </span>
                                        )}
                                    </td>
                                    <td>{u.status === 'suspended' ? 'Yes' : 'No'}</td>
                                    <td>{new Date(u.created_at).toLocaleString()}</td>
                                    <td>
                                        {u.last_login && u.last_login !== 'Never'
                                            ? new Date(u.last_login).toLocaleString()
                                            : u.last_login || '—'}
                                    </td>
                                    <td>
                                        {!canEdit ? '—' : u.status === 'active' ? (
                                            <button className="action-btn suspend" disabled={isActioning} onClick={() => handleSuspend(u)}>🚫 Suspend</button>
                                        ) : (
                                            <>
                                                <button className="action-btn restore" disabled={isActioning} onClick={() => handleRestore(u)}>Restore</button>
                                                <button className="action-btn delete"  disabled={isActioning} onClick={() => handleDelete(u)}>Delete</button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {showInviteModal && (
                <InviteModal
                    isOpen={showInviteModal}
                    onClose={() => setShowInviteModal(false)}
                    orgRoles={orgRoles}
                    profileTemplates={profileTemplates}
                    onInvitesSent={() => { setStatus('Invites sent successfully.'); loadInvitations(); }}
                />
            )}

            </>
            )}

            {/* Role-action failures raised outside a modal (e.g. the Users-tab
                role dropdown) — a pop-out, not the transient status line. */}
            <InfoModal
                isOpen={!!roleActionError}
                title={roleActionError?.title || ''}
                message={roleActionError?.message || ''}
                onClose={() => setRoleActionError(null)}
            />
        </div>
    );
}

function EmojiButton({ emoji, onClick, disabled }) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            style={{
                background: 'none', border: 'none', padding: 0, margin: 0,
                fontSize: '24px', cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.4 : 1, outline: 'none',
                transition: 'transform 0.15s ease-in-out',
            }}
            onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.transform = 'scale(1.2)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
        >
            {emoji}
        </button>
    );
}